'use client';

import React from 'react';
import { Button } from '@/components/Buttons/Button';
import { Icon } from '@/components/Icons/Icon';
import { RECONNECT_MAX_RETRIES } from '../constants';
import type { AvatarState, ConnectionState } from '../types';

export interface ConnectionOverlayProps {
  connectionState: ConnectionState;
  avatarState: AvatarState;
  retryCount: number;
  errorMessage: string;
  isSmUp: boolean;
  /** Start the conversation (initial connect). */
  onStart: () => void;
  /** Cancel an in-progress reconnect. */
  onCancel: () => void;
  /** Retry after an error. */
  onRetry: () => void;
  /** Exit the simulation. */
  onExit: () => void;
}

/**
 * Centered overlay shown over the avatar stage while the session is
 * connecting/reconnecting, after an error, or before the user has started.
 * The caller is responsible for gating visibility (it is hidden once the
 * avatar is active or an audio-only visualization is shown).
 */
export const ConnectionOverlay: React.FC<ConnectionOverlayProps> = ({
  connectionState,
  avatarState,
  retryCount,
  errorMessage,
  isSmUp,
  onStart,
  onCancel,
  onRetry,
  onExit,
}) => (
  <div className="absolute inset-0 flex items-center justify-center">
    {connectionState === 'connecting' ||
    connectionState === 'reconnecting' ||
    connectionState === 'connected' ||
    connectionState === 'ready' ||
    avatarState === 'connecting' ? (
      <div className="flex flex-col items-center gap-4">
        <Icon
          name="CircleNotch"
          size={48}
          className="animate-spin-clockwise animate-duration-1000 animate-iteration-count-infinite text-brand-primary"
        />
        <p className="typography-body text-semantic-text-primary">
          {connectionState === 'connecting'
            ? 'Connecting to session...'
            : connectionState === 'reconnecting'
              ? `Reconnecting... (${retryCount}/${RECONNECT_MAX_RETRIES})`
              : connectionState === 'connected'
                ? 'Initializing...'
                : avatarState === 'connecting'
                  ? 'Starting avatar...'
                  : 'Preparing avatar...'}
        </p>
        {connectionState === 'reconnecting' && (
          <Button variant="tertiary" onClick={onCancel} className="mt-2">
            Cancel
          </Button>
        )}
      </div>
    ) : errorMessage ? (
      <div className="flex flex-col items-center gap-4 max-w-md px-4">
        <Icon name="Warning" size={48} className="text-accent-status-red" />
        <p className="typography-body text-center text-semantic-text-primary">{errorMessage}</p>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onRetry} className="h-14 px-8 py-5">
            Retry
          </Button>
          <Button variant="tertiary" onClick={onExit} className="h-14 px-8 py-5">
            Exit
          </Button>
        </div>
      </div>
    ) : (
      <Button variant="secondary" onClick={onStart} size={isSmUp ? 'medium' : 'small'}>
        <span className="sm:hidden">Start</span>
        <span className="hidden sm:inline">🎭 Start Conversation</span>
      </Button>
    )}
  </div>
);
